/**
 * Tabela "parcelas ano a ano": a parcela do primeiro mês de cada ano do plano,
 * por cenário, mais uma linha no primeiro mês depois da contemplação — é ali
 * que a parcela muda, e o cliente precisa ver o salto sem esperar o ano virar.
 *
 * As séries vêm de parcelasDoPlano (resumoCliente.ts), então a soma desta
 * tabela é o mesmo "total pago" do resumo.
 */
import type { CenarioResumo } from './resumoCliente';

export type LinhaAnoAno = {
  rotulo: string;
  /** Mês do plano que a linha mostra (1 = primeiro). */
  mes: number;
  posContemplacao: boolean;
  /** null quando o cenário já quitou o plano nesse mês. */
  valores: Record<CenarioResumo, number | null>;
};

export const tabelaAnoAno = (
  series: Record<CenarioResumo, number[]>,
  mesContemplacao: number,
): LinhaAnoAno[] => {
  const cenarios = Object.keys(series) as CenarioResumo[];
  const duracao = Math.max(0, ...cenarios.map((c) => series[c].length));
  const valoresNoMes = (mes: number) =>
    Object.fromEntries(cenarios.map((c) => [c, series[c][mes - 1] ?? null])) as Record<CenarioResumo, number | null>;

  const meses: Array<{ mes: number; pos: boolean }> = [];
  for (let mes = 1; mes <= duracao; mes += 12) meses.push({ mes, pos: false });
  const primeiroPos = mesContemplacao + 1;
  if (mesContemplacao >= 1 && primeiroPos <= duracao) {
    const mesmo = meses.find((m) => m.mes === primeiroPos);
    if (mesmo) mesmo.pos = true;
    else meses.push({ mes: primeiroPos, pos: true });
  }
  meses.sort((a, b) => a.mes - b.mes);

  return meses.map(({ mes, pos }) => ({
    rotulo: pos && (mes - 1) % 12 !== 0 ? `Mês ${mes}` : `Ano ${Math.floor((mes - 1) / 12) + 1}`,
    mes,
    posContemplacao: pos,
    valores: valoresNoMes(mes),
  }));
};

export const totaisDasSeries = (series: Record<CenarioResumo, number[]>): Record<CenarioResumo, number> =>
  Object.fromEntries(
    Object.entries(series).map(([c, s]) => [c, s.reduce((a, b) => a + b, 0)]),
  ) as Record<CenarioResumo, number>;
